import React, { useState, useEffect } from 'react';
import { TextField } from '@mui/material';

/**
 * Componente TextField con validación incorporada
 * 
 * @param {string} value - Valor del campo
 * @param {function} onChange - Callback cuando cambia el valor
 * @param {string} label - Etiqueta del campo
 * @param {function} validator - Función de validación que retorna true/false
 * @param {string} errorMessage - Mensaje de error personalizado
 * @param {boolean} required - Si es requerido
 * @param {boolean} validateOnBlur - Si valida al perder el foco (default: true)
 * @param {boolean} validateOnChange - Si valida mientras escribe (default: false)
 * @param {object} inputProps - Props de input para restringir caracteres (ej: { pattern: '[0-9-]*' })
 * @param {object} textFieldProps - Props adicionales para TextField
 */
const ValidatedTextField = ({
  value,
  onChange,
  label,
  validator,
  errorMessage = 'Valor inválido',
  required = false,
  validateOnBlur = true,
  validateOnChange = false,
  inputProps = {},
  ...textFieldProps
}) => {
  const [error, setError] = useState('');
  const [touched, setTouched] = useState(false);

  // Validar el valor
  const validate = (val) => {
    if (required && !val) {
      return `${label} es requerido`;
    }
    if (val && validator && !validator(val)) {
      return errorMessage;
    }
    return '';
  };

  // Efecto para validar cuando cambia el valor (si validateOnChange es true)
  useEffect(() => {
    if (validateOnChange && touched) {
      setError(validate(value));
    }
  }, [value, validateOnChange, touched]);

  // Manejar cambio
  const handleChange = (e) => {
    let newValue = e.target.value;
    
    // Si hay un patrón de input, aplicar restricción
    if (inputProps.pattern) {
      const regex = new RegExp(inputProps.pattern);
      newValue = newValue.split('').filter(char => regex.test(char)).join('');
    }
    
    onChange(newValue);
    if (!touched && validateOnChange) {
      setTouched(true);
      setError(validate(newValue));
    }
  };

  // Manejar blur
  const handleBlur = (e) => {
    if (!touched) {
      setTouched(true);
    }
    if (validateOnBlur) {
      setError(validate(e.target.value));
    }
  };

  return (
    <TextField
      {...textFieldProps}
      label={label}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      error={!!error}
      helperText={error}
      required={required}
      inputProps={inputProps}
    />
  );
};

export default ValidatedTextField;